/*
project_utils.js
This module contains project-specific utility functions for the Print Solutions.
*/
import merge from "lodash/merge";
import {
  classifyPriceFailure,
  classifyThrownError,
  PRICE_ERROR_KIND,
  PriceProviderError,
} from "@/lib/print/price-errors";
import {
  buildPriceCacheKey,
  priceBackendFor,
  regionFor,
} from "@/lib/print/pricing-key";
import { cachedPreset, cachedProductData } from "@/lib/print/runtime-cache";
import { createClient as createBrowserClient } from "@/lib/supabase/client";
import { cloneDeep, getLanguageValue, resolveLocaleValue } from "@/lib/utils";

function boundaryClient(options) {
  return options?.supabase ?? createBrowserClient();
}

// Merge admin and user modifications
export function mergeAdminAndUserModifications(
  adminModifications,
  userModifications,
  defaultMargin = 0
) {
  adminModifications = adminModifications || {};
  if (!userModifications) return adminModifications;

  const mergedModifications = merge(
    cloneDeep(adminModifications),
    userModifications
  );

  // Keep source precedence for display; editable maps retain their existing shape.
  mergedModifications.title_overrides = {
    user: cloneDeep(userModifications.user_translated_titles || {}),
    central: cloneDeep(adminModifications?.user_translated_titles || {}),
  };

  // Handle props that have to be overwritten instead of deep merged
  // user_gallery
  mergedModifications.user_gallery =
    userModifications.user_gallery || adminModifications.user_gallery;

  // Handle category_ids array
  mergedModifications.category_ids =
    userModifications.category_ids || adminModifications.category_ids || [];

  mergedModifications.margin = mergedModifications.margin || defaultMargin;

  mergedModifications.copy_options =
    mergedModifications.copy_options || adminModifications.copy_options;

  return mergedModifications;
}

export async function getUserDataFromEmbedOrUser(
  embedId,
  userId,
  userModId,
  options
) {
  const supabase = boundaryClient(options);
  // If userId is not provided, try to resolve it from userModId or embedId
  if (!userId) {
    if (userModId) {
      // Fetch user_id from user_product_modifications using userModId
      const { data: userModData, error: userModError } = await supabase
        .from("user_product_modifications")
        .select("user_id")
        .eq("id", userModId)
        .single();
      if (userModError) throw userModError;
      userId = userModData.user_id;
    } else if (embedId) {
      const { resolvePublicEmbedIdentifier } = await import(
        "@/lib/embeds/public-identifier"
      );
      const resolution = await resolvePublicEmbedIdentifier(String(embedId), {
        supabase,
      });
      if (!resolution.ok) {
        const error = new Error(resolution.code);
        error.code = resolution.code;
        error.status = resolution.status;
        throw error;
      }
      userId = resolution.userId;
    } else {
      throw new Error(
        "At least one of userId, userModId, or embedId must be provided"
      );
    }
  }

  // Fetch user data using the user_id
  const { data: userData, error: userError } = await supabase
    .from("profiles")
    .select("id, language, locale, margin, currency, catalogue_mode")
    .eq("id", userId)
    .single();

  if (userError) throw userError;

  return { ...userData, user_id: userId };
}

export async function getAdminProductModifications(adminSku, options) {
  const supabase = boundaryClient(options);
  const { data, error } = await supabase
    .from("product_modifications")
    .select("*")
    .eq("admin_sku", adminSku)
    .maybeSingle();

  if (error) throw error;
  return data || {};
}

export async function getUserProductModifications(userId, adminSku, options) {
  const supabase = boundaryClient(options);
  const { data, error } = await supabase
    .from("user_product_modifications")
    .select("*,   product_modifications (*)")
    .eq("user_id", userId)
    .eq("admin_sku", adminSku)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function getUserProductModificationById(id, options) {
  const supabase = boundaryClient(options);
  const { data, error } = await supabase
    .from("user_product_modifications")
    .select("*,   product_modifications (*)")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function updateUserProductModificationById(
  id,
  modifications,
  options
) {
  const supabase = boundaryClient(options);
  const { data, error } = await supabase
    .from("user_product_modifications")
    .update(modifications)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getUserProductsModifications(
  userId,
  sortConfig,
  campaignId = null,
  campaignSlug = null,
  options
) {
  const supabase = boundaryClient(options);
  // When filtering by campaign, we need to fetch campaign_products with index and sort in-memory
  // because PostgREST doesn't support ordering by joined table columns directly
  // Special case: 'main' campaign should sort by user_product_modifications.index

  let baseSelect = `
    *,
    product_modifications (*),
    campaign_products(campaign_id, index)
  `;

  let query = supabase
    .from("user_product_modifications")
    .select(baseSelect)
    .eq("user_id", userId);

  // Check if this is the 'main' campaign or "all" - both should use user_product_modifications.index
  // Apply campaign filtering if provided and not "all" or "main"
  if (campaignId && campaignId !== "all") {
    // Use inner join to filter by specific campaign
    query = query
      .select(
        `
        *,
        product_modifications (*),
        campaign_products!inner(campaign_id, index)
      `
      )
      .eq("campaign_products.campaign_id", campaignId);
  } else {
    // When not filtering by campaign or when using 'main' campaign, sort by user_product_modifications.index
    if (sortConfig?.key) {
      const { key, direction } = sortConfig;
      const keyLower = key.toLowerCase();
      if (keyLower === "visible") {
        query = query.order("visible", { ascending: direction === "asc" });
      } else if (keyLower === "index") {
        query = query.order("index", { ascending: direction === "asc" });
      }
    } else {
      query = query.order("index", { ascending: true });
    }
  }

  const { data, error } = await query;
  if (error) throw error;

  // If filtering by a specific campaign (not 'main' or 'all'), sort in-memory by campaign_products.index
  if (campaignId && campaignId !== "all" && campaignSlug !== "main" && data) {
    const sortDirection = sortConfig?.direction === "desc" ? -1 : 1;
    data.sort((a, b) => {
      const aIndex = a.campaign_products?.[0]?.index ?? Infinity;
      const bIndex = b.campaign_products?.[0]?.index ?? Infinity;
      return (aIndex - bIndex) * sortDirection;
    });
  }

  return data || [];
}

export async function getVisibleUserProductsModifications(
  userId,
  categoryId = null,
  sortConfig = null,
  campaignId = null,
  campaignSlug = null,
  options
) {
  const supabase = boundaryClient(options);
  try {
    // When filtering by campaign, we need to fetch campaign_products with index and sort in-memory
    // because PostgREST doesn't support ordering by joined table columns directly
    // Special case: 'main' campaign should sort by user_product_modifications.index

    let baseSelect = `
      *,
      product_modifications (*),
      campaign_products(campaign_id, index)
    `;

    // Check if we should use user_product_modifications.index (only when campaignId is "all" or not provided)
    const useUserModificationIndex =
      campaignSlug === "main" || campaignId === "all" || !campaignId;

    // If filtering by specific campaign (not 'all'), use inner join
    if (campaignId && campaignId !== "all") {
      baseSelect = `
        *,
        product_modifications (*),
        campaign_products!inner(campaign_id, index)
      `;
    }

    let query = supabase
      .from("user_product_modifications")
      .select(baseSelect)
      .eq("user_id", userId)
      .eq("visible", true);

    // Apply campaign filtering if provided and not "all"
    if (campaignId && campaignId !== "all") {
      query = query.eq("campaign_products.campaign_id", campaignId);
    }
    if (categoryId && categoryId !== "all") {
      // Use the category_ids array column for filtering
      query = query.contains("category_ids", [categoryId]);
    }

    // Apply sorting if provided (only when NOT filtering by specific campaign, or when using 'main')
    if (useUserModificationIndex) {
      if (sortConfig?.key) {
        const { key, direction } = sortConfig;
        const keyLower = key.toLowerCase();
        if (keyLower === "visible") {
          query = query.order("visible", { ascending: direction === "asc" });
        } else if (keyLower === "index") {
          query = query.order("index", { ascending: direction === "asc" });
        }
      } else {
        query = query.order("index", { ascending: true });
      }
    }

    const { data, error } = await query;
    if (error) throw error;

    // If filtering by a specific campaign (not 'all'), sort in-memory by campaign_products.index
    if (campaignId && campaignId !== "all" && data && campaignSlug !== "main") {
      const sortDirection = sortConfig?.direction === "desc" ? -1 : 1;
      data.sort((a, b) => {
        const aIndex = a.campaign_products?.[0]?.index ?? Infinity;
        const bIndex = b.campaign_products?.[0]?.index ?? Infinity;
        return (aIndex - bIndex) * sortDirection;
      });
    }

    return data || [];
  } catch (error) {
    console.error("Error in getVisibleUserProductsModifications:", error);
    throw error;
  }
}

/**
 * Shared core for fetching user product modifications with campaign resolution.
 * Returns raw modifications + userData. Callers handle pagination, merging, and margin stripping.
 */
export async function fetchUserProductModifications(
  userId,
  {
    campaignSlug = "all",
    sortBy = null,
    sortDirection = null,
    category = "all",
  } = {},
  options
) {
  const supabase = boundaryClient(options);
  const userData = await getUserDataFromEmbedOrUser(
    null,
    userId,
    null,
    options
  );

  // Resolve campaign slug → ID
  let campaignId = "all";
  if (campaignSlug && campaignSlug !== "all") {
    const { data: campaign, error: campaignError } = await supabase
      .from("campaigns")
      .select("id")
      .eq("user_id", userData.id)
      .eq("slug", campaignSlug)
      .single();

    if (campaignError) {
      return { userModifications: [], userData };
    }
    if (campaign) campaignId = campaign.id;
  }

  const sortConfig =
    sortBy && sortDirection
      ? {
          key: sortBy.charAt(0).toUpperCase() + sortBy.slice(1),
          direction: sortDirection,
        }
      : null;

  const userModifications = await getVisibleUserProductsModifications(
    userData.id,
    category,
    sortConfig,
    campaignId,
    campaignSlug,
    options
  );

  return { userModifications, userData };
}

/**
 * Merges a list of user modifications with their admin modifications.
 * Returns products WITH margin data. Callers strip margin if needed (e.g. widget).
 */
export function mergeUserModifications(userMods, defaultMargin) {
  return userMods.map((userMod) =>
    mergeAdminAndUserModifications(
      userMod.product_modifications,
      userMod,
      defaultMargin
    )
  );
}

export async function fetchProductDataFromPrintApi(
  sku,
  view = "reseller",
  locale = null,
  options
) {
  return cachedProductData({ sku, view, locale }, async () => {
    try {
      const localeParam = locale ? `&locale=${locale}` : "";
      const response = await fetch(
        `${
          process.env.NEXT_PUBLIC_BASE_URL
        }/api/print/proxy?endpoint=products/${sku}${view ? "?view=" + view : ""}${localeParam}`,
        {
          headers: options?.headers,
        }
      );

      if (!response.ok) {
        throw new Error(`Network response was not ok for SKU: ${sku}`);
      }
      return response.json();
    } catch (e) {
      console.log("response error");
      console.log(e);
      return {};
    }
  });
}

// Price cache: stores raw print.com responses (before margin). The key is built
// by buildPriceCacheKey and includes the upstream backend, so the two endpoints
// with their different response shapes can never share a bucket.
const PRICE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const PRICE_CACHE_MAX_SIZE = 1000;
const priceCache = new Map(); // key → { data, expiry }
const inflight = new Map(); // key → Promise (deduplicates concurrent requests)

export function getPriceCacheKey(userId, sku, configuration, currency, locale) {
  return buildPriceCacheKey({
    backend: priceBackendFor(currency),
    userId,
    sku,
    configuration,
    currency,
    locale,
  });
}

function evictExpiredEntries() {
  const now = Date.now();
  for (const [key, entry] of priceCache) {
    if (now > entry.expiry) priceCache.delete(key);
  }
}

export async function fetchPriceDataAndApplyMargin(
  print_sku,
  margin,
  configuration,
  { userId, embedId, currency, locale },
  options
) {
  const cacheKey = getPriceCacheKey(
    userId,
    print_sku,
    configuration,
    currency,
    locale
  );

  // Check cache for raw print.com response
  const cached = priceCache.get(cacheKey);
  if (cached && Date.now() < cached.expiry) {
    return applyMarginToPriceData(cached.data, margin);
  }

  // Deduplicate concurrent requests for the same key
  if (inflight.has(cacheKey)) {
    const rawData = await inflight.get(cacheKey);
    return applyMarginToPriceData(rawData, margin);
  }

  const fetchPromise = fetchRawPriceData(
    print_sku,
    configuration,
    {
      userId,
      embedId,
      currency,
      locale,
    },
    options?.headers
  );
  inflight.set(cacheKey, fetchPromise);

  try {
    const rawData = await fetchPromise;

    // Only successful responses are cached. A failure of any kind must stay
    // out of the cache so the next caller retries the upstream.
    if (rawData) {
      if (priceCache.size >= PRICE_CACHE_MAX_SIZE) evictExpiredEntries();
      if (priceCache.size >= PRICE_CACHE_MAX_SIZE) {
        // Still full after eviction - delete oldest entry
        const oldestKey = priceCache.keys().next().value;
        priceCache.delete(oldestKey);
      }
      priceCache.set(cacheKey, {
        data: rawData,
        expiry: Date.now() + PRICE_CACHE_TTL,
      });
    }

    return applyMarginToPriceData(rawData, margin);
  } finally {
    inflight.delete(cacheKey);
  }
}

async function fetchFromPrintProxy(url, options) {
  try {
    return await fetch(url, options);
  } catch (error) {
    throw new PriceProviderError({
      kind: classifyThrownError(error),
      message: error?.message,
      cause: error,
    });
  }
}

async function throwClassifiedPriceFailure(response) {
  const body = await response.json().catch(() => ({}));
  throw new PriceProviderError({
    kind: classifyPriceFailure({ status: response.status, body }),
    status: response.status,
    body,
  });
}

async function fetchRawPriceData(
  print_sku,
  configuration,
  { userId, embedId, currency, locale },
  headers
) {
  const baseUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/api/print/proxy`;
  const url = new URL(baseUrl);

  if (userId) url.searchParams.append("userId", userId);
  if (embedId) url.searchParams.append("embedId", embedId);

  // Currency selects the multi-currency batch endpoint.
  if (currency) {
    url.searchParams.append("endpoint", "batch-prices");

    const region = regionFor(locale);

    const response = await fetchFromPrintProxy(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify({
        requests: [
          { sku: print_sku, options: configuration, region, currency },
        ],
      }),
    });

    if (!response.ok) {
      await throwClassifiedPriceFailure(response);
    }

    const batchData = await response.json();
    const batchPrice = batchData?.responses?.[0]?.price?.price;

    // A missing price for an otherwise-successful response means the provider
    // could not price this configuration (invalid/unavailable combination).
    if (batchPrice == null) {
      throw new PriceProviderError({
        kind: PRICE_ERROR_KIND.UNPRICEABLE,
        status: response.status,
        body: batchData?.responses?.[0] ?? batchData,
      });
    }

    // Batch endpoint returns integer cents - convert to decimal
    return { prices: { salesPrice: batchPrice / 100 } };
  }

  // Requests without currency use the EUR-only single-product endpoint.
  url.searchParams.append("endpoint", `products/${print_sku}/price`);

  const response = await fetchFromPrintProxy(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify({ sku: print_sku, options: configuration }),
  });

  if (!response.ok) {
    await throwClassifiedPriceFailure(response);
  }

  return response.json();
}

function applyMarginToPriceData(priceData, margin) {
  const applyMargin = (price) => price * (1 + margin);
  const roundToTwoDecimals = (num) => Math.round(num * 100) / 100;
  return {
    ...priceData,
    prices: Object.entries(priceData.prices).reduce((acc, [key, value]) => {
      acc[key] = roundToTwoDecimals(applyMargin(value));
      return acc;
    }, {}),
  };
}

/**
 * Returns the preset configuration, or `null` when the SKU genuinely has none
 * (callers degrade that row). A systemic failure - auth, rate limit, outage,
 * network, timeout - throws a PriceProviderError instead of being flattened
 * into `null`, so an outage cannot masquerade as "this product has no preset".
 */
export async function fetchMainPrintProductPreset(print_sku, options) {
  return cachedPreset(print_sku, async () => {
    const response = await fetchFromPrintProxy(
      `${process.env.NEXT_PUBLIC_BASE_URL}/api/print/proxy?useWebApi=true&endpoint=presets/${print_sku}`,
      {
        headers: options?.headers,
      }
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const kind = classifyPriceFailure({ status: response.status, body });

      if (kind !== PRICE_ERROR_KIND.UNPRICEABLE) {
        throw new PriceProviderError({ kind, status: response.status, body });
      }

      return null;
    }

    let _presets;
    try {
      _presets = await response.json();
    } catch (error) {
      throw new PriceProviderError({
        kind: PRICE_ERROR_KIND.UPSTREAM,
        status: response.status,
        message: `Unreadable preset response for SKU: ${print_sku}`,
        cause: error,
      });
    }

    if (!_presets?.items?.length || !_presets.items[0].configuration) {
      return null;
    }

    const configuration = _presets.items[0].configuration;
    return Object.keys(configuration).reduce((a, v) => {
      if (configuration[v] != null && v != "delivery") a[v] = configuration[v];
      return a;
    }, {});
  });
}

// New visibility functions

export async function setProductsVisibility(
  table,
  admin_skus,
  visible,
  options
) {
  const supabase = boundaryClient(options);
  const { data, error } = await supabase
    .from(table)
    .update({ visible })
    .in("admin_sku", admin_skus);

  if (error) throw error;
  return data;
}

export async function setProductsVisibilityById(table, ids, visible, options) {
  const supabase = boundaryClient(options);
  const { data, error } = await supabase
    .from(table)
    .update({ visible })
    .in("id", ids);

  if (error) throw error;
  return data;
}

export async function setSelectedVisible(table, admin_skus, options) {
  return setProductsVisibility(table, admin_skus, true, options);
}

export async function setSelectedHidden(table, admin_skus, options) {
  return setProductsVisibility(table, admin_skus, false, options);
}

export async function setSelectedVisibleById(table, ids, options) {
  return setProductsVisibilityById(table, ids, true, options);
}

export async function setSelectedHiddenById(table, ids, options) {
  return setProductsVisibilityById(table, ids, false, options);
}

/**
 * Returns the value for the desired language or falls back to English ('en').
 * @param {Object} languageObject - The object containing language values.
 * @param {string} desiredLanguage - The desired language key.
 * @returns {string} - The value for the desired language or the fallback language.
 */
export function getLocalizedValue(
  languageObject,
  desiredLanguage = "en-GB",
  fallBack
) {
  if (typeof languageObject !== "object") {
    console.warn("getLocalizedValue should receive an object");
    return fallBack || languageObject;
  }
  return resolveLocaleValue(languageObject, desiredLanguage) ?? fallBack ?? "";
}

/**
 * Returns a product title for the desired locale.
 * Fallback chain:
 *   modification[locale] → modification[sibling region, e.g. de-AT → de-DE]
 *   → API value (locale-aware) → modification[en-GB]
 * The sibling-region step matters because the catalog stores titles under the
 * import locale (e.g. "de-DE") while a reseller may run on "de-AT"; without it
 * the title falls through to English even though the German value exists.
 */
export function getLocalizedTitleValue(translationsObj, locale, apiFallback) {
  const sameLanguage = getLanguageValue(translationsObj, locale);
  if (sameLanguage) return sameLanguage;
  if (apiFallback) return apiFallback;
  return getLanguageValue(translationsObj, "en-GB") || "";
}

// Split the admin SKU by '-' and remove the last part (the hash)
export function adminSkuToSku(adminSku) {
  const parts = adminSku.split("-");
  return parts.slice(0, -1).join("-");
}

/**
 * Sets or updates the value for a specific language in the languageObject.
 * If the value is an empty string, it removes the language key from the object.
 * @param {Object} languageObject - The object containing language values.
 * @param {string} language - The language key to set or update.
 * @param {string} value - The value to set for the language.
 * @returns {Object} - The updated languageObject.
 */
export function setLocalizedValue(languageObject, language, value) {
  const updatedObject = { ...languageObject };
  if (value === "") {
    delete updatedObject[language];
  } else {
    updatedObject[language] = value;
  }
  return updatedObject;
}

export async function fetchCategories(options) {
  const supabase = boundaryClient(options);
  try {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("name");

    if (error) throw error;

    return data;
  } catch (error) {
    console.error("Error fetching categories:", error.message);
    throw error;
  }
}

export async function fetchVisibleEmbedCategories(
  embedId,
  filterOutFeatured,
  resolvedUserId = null,
  options
) {
  const supabase = boundaryClient(options);
  try {
    const userId =
      resolvedUserId ||
      (await getUserDataFromEmbedOrUser(embedId, null, null, options)).user_id;

    // Get visible products with their category_ids
    const { data: visibleProducts, error: productsError } = await supabase
      .from("user_product_modifications")
      .select("category_ids")
      .eq("user_id", userId)
      .eq("visible", true);

    if (productsError) throw productsError;

    // Get unique category IDs from all products
    const uniqueCategoryIds = [
      ...new Set(
        visibleProducts.flatMap((product) => product.category_ids || [])
      ),
    ];

    if (uniqueCategoryIds.length === 0) return [];

    // Filter out featured category if requested
    const filteredCategoryIds = filterOutFeatured
      ? uniqueCategoryIds.filter(
          (id) => id !== "88fdf32d-a886-4562-88b1-ffde16508e37"
        )
      : uniqueCategoryIds;

    // Fetch category details for these IDs
    const { data: categories, error: categoriesError } = await supabase
      .from("categories")
      .select("id, name, slug")
      .in("id", filteredCategoryIds);

    if (categoriesError) throw categoriesError;

    return categories;
  } catch (error) {
    console.error("Error fetching user categories:", error.message);
    throw error;
  }
}

export async function getKeenId(userId, options) {
  const baseUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/api/print/proxy`;
  const url = new URL(baseUrl);

  // Add userId and embedId if they exist
  if (userId) url.searchParams.append("userId", userId);

  // Add endpoint parameter for user profile
  url.searchParams.append("endpoint", "user/profile");

  // Make API request to print
  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData));
  }

  const profileData = (await response.json()) || {};
  return profileData?.customerId;
}

/**
 * Maps locale codes to their corresponding country calling codes and expected phone number lengths
 */
export const LOCALE_PHONE_CONFIGS = {
  "nl-NL": { countryCode: "+31", minLength: 10, maxLength: 13 }, // Netherlands
  "nl-BE": { countryCode: "+32", minLength: 9, maxLength: 13 }, // Belgium (Dutch)
  "fr-BE": { countryCode: "+32", minLength: 9, maxLength: 13 }, // Belgium (French)
  "fr-FR": { countryCode: ["+33", "+35"], minLength: 9, maxLength: 13 }, // France (supports +33 and +35)
  "de-DE": { countryCode: "+49", minLength: 10, maxLength: 13 }, // Germany
  "es-ES": { countryCode: "+34", minLength: 9, maxLength: 13 }, // Spain
  "en-GB": { countryCode: "+44", minLength: 10, maxLength: 13 }, // United Kingdom
  "de-AT": { countryCode: "+43", minLength: 10, maxLength: 13 }, // Austria
};

/**
 * Validates and formats a phone number based on locale
 * @param {string} phoneNumber - The phone number to validate
 * @param {string} locale - The locale (e.g., "nl-NL", "en-GB")
 * @returns {Object} - Result with isValid, formattedNumber, and optional error
 */
export function validatePhoneNumber(phoneNumber, locale = "en-GB") {
  if (!phoneNumber) {
    return {
      isValid: false,
      error: "Phone number is required",
    };
  }

  // Get the configuration for the specified locale or fall back to en-GB
  const phoneConfig =
    LOCALE_PHONE_CONFIGS[locale] || LOCALE_PHONE_CONFIGS["en-GB"];

  // Clean the phone number by removing all whitespace and special characters except +
  let cleanedNumber = phoneNumber.replace(/\s+/g, "").replace(/[^0-9+]/g, "");

  // Check if the phone number starts with a country code
  let hasCountryCode = cleanedNumber.startsWith("+");

  // Handle multiple country codes for a locale (like fr-FR with +33 and +35)
  const countryCodes = Array.isArray(phoneConfig.countryCode)
    ? phoneConfig.countryCode
    : [phoneConfig.countryCode];

  // If no country code is present, add the appropriate country code based on locale
  if (!hasCountryCode) {
    // Check if the number might start with a country code without the + symbol
    // For example, if someone enters 31612345678 instead of +31612345678
    let foundMatchingCode = false;

    for (const countryCode of countryCodes) {
      const countryCodeDigits = countryCode.substring(1);

      if (cleanedNumber.startsWith(countryCodeDigits)) {
        // Already has the country code digits, just add the + symbol
        cleanedNumber = "+" + cleanedNumber;
        foundMatchingCode = true;
        break;
      }
    }

    if (!foundMatchingCode) {
      // Doesn't have any country code, so add the first (primary) country code
      cleanedNumber = countryCodes[0] + cleanedNumber;
    }
  }

  // Now remove the country code from the number to check national number length
  let nationalNumber = cleanedNumber;
  let matchedCountryCode = null;

  // Go through each country code to see if the number starts with one
  for (const [, config] of Object.entries(LOCALE_PHONE_CONFIGS)) {
    const configCountryCodes = Array.isArray(config.countryCode)
      ? config.countryCode
      : [config.countryCode];

    for (const countryCode of configCountryCodes) {
      if (cleanedNumber.startsWith(countryCode)) {
        nationalNumber = cleanedNumber.substring(countryCode.length);
        matchedCountryCode = countryCode;
        break;
      }
    }

    if (matchedCountryCode) break;
  }

  // Use the matched country code length, or fall back to the first country code
  const referenceCountryCode = matchedCountryCode || countryCodes[0];

  // Check if the national part of the number is within expected length
  const isValidLength =
    nationalNumber.length >=
      phoneConfig.minLength - referenceCountryCode.length + 1 &&
    nationalNumber.length <=
      phoneConfig.maxLength - referenceCountryCode.length + 1;

  if (!isValidLength) {
    return {
      isValid: false,
      formattedNumber: cleanedNumber,
      error: `Phone number should have between ${phoneConfig.minLength - referenceCountryCode.length + 1} and ${phoneConfig.maxLength - referenceCountryCode.length + 1} digits (excluding country code)`,
    };
  }

  // If everything is valid
  return {
    isValid: true,
    formattedNumber: cleanedNumber,
    error: null,
  };
}

/**
 * Generates a print.com URL for a given admin_sku by combining default presets with user-picked defaults
 * @param {string} admin_sku - The admin SKU to generate the print URL for
 * @returns {Promise<string>} - The generated print.com URL
 */
export async function generatePrintUrl(admin_sku, options) {
  const supabase = boundaryClient(options);
  try {
    let user_picked_defaults = {};
    let sku = null;

    // Try to query the product_modifications table to get the row with matching admin_sku
    const { data: productModification, error } = await supabase
      .from("product_modifications")
      .select("user_picked_defaults, sku")
      .eq("admin_sku", admin_sku)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (productModification) {
      // If we found a matching row, use its data
      user_picked_defaults = productModification.user_picked_defaults || {};
      sku = productModification.sku;
    } else {
      // If no matching row found, we need to derive t he SKU from admin_sku
      // Using the existing adminSkuToSku function
      sku = adminSkuToSku(admin_sku);
    }

    if (!sku) {
      throw new Error(`Could not determine SKU from admin_sku: ${admin_sku}`);
    }

    // Fetch the default preset for the SKU
    const defaultPreset = await fetchMainPrintProductPreset(sku, options);

    // Combine default preset with user-picked defaults
    const printOptions = {
      ...(defaultPreset || {}),
      ...user_picked_defaults,
    };

    // Generate query parameters
    const queryParams = new URLSearchParams(printOptions);

    // Generate the final URL
    const fullUrl = `https://app.print.com/selector/${sku}?${queryParams.toString()}`;

    return fullUrl;
  } catch (error) {
    console.error(
      `Error generating print URL for admin_sku ${admin_sku}:`,
      error
    );
    throw error;
  }
}
